import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Wallet, TrendingUp, CreditCard, AlertTriangle, Download, Eye, Flag } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { StatsCard } from '../components/StatsCard';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
const transactions = [{
  id: 'TRX-8921',
  type: 'Ride Fare',
  party: 'John Doe (Rider)',
  amount: '+₦45.00',
  status: 'completed',
  date: '2 min ago'
}, {
  id: 'TRX-8920',
  type: 'Rider Payout',
  party: 'Mike Ross',
  amount: '-₦850.00',
  status: 'completed',
  date: '1 hour ago'
}, {
  id: 'TRX-8919',
  type: 'Refund',
  party: 'Charlie Brown (User)',
  amount: '-₦15.00',
  status: 'completed',
  date: '2 hours ago'
}, {
  id: 'TRX-8918',
  type: 'Ride Fare',
  party: 'Bruce Wayne (Rider)',
  amount: '+₦28.00',
  status: 'completed',
  date: '3 hours ago'
}, {
  id: 'TRX-8917',
  type: 'Platform Fee',
  party: 'System',
  amount: '+₦4.50',
  status: 'completed',
  date: '3 hours ago'
}, {
  id: 'TRX-8916',
  type: 'Ride Fare',
  party: 'Clark Kent (Rider)',
  amount: '+₦12.00',
  status: 'pending',
  date: '4 hours ago'
}, {
  id: 'TRX-8915',
  type: 'Wallet Adjustment',
  party: 'Alice Smith (User)',
  amount: '+₦50.00',
  status: 'completed',
  date: '5 hours ago'
}, {
  id: 'TRX-8914',
  type: 'Commission',
  party: 'System',
  amount: '+₦125.00',
  status: 'completed',
  date: '6 hours ago'
}];
const flaggedEarnings = [{
  id: 1,
  rider: 'Sarah Connor',
  reason: 'Unusually high earnings (₦450 in 2 hours)',
  date: 'Today, 10:00 AM',
  status: 'under_review'
}, {
  id: 2,
  rider: 'Barry Allen',
  reason: 'Multiple short trips < 1 min',
  date: 'Yesterday',
  status: 'cleared'
}, {
  id: 3,
  rider: 'Arthur Curry',
  reason: 'Suspicious tip amount (₦100)',
  date: '2 days ago',
  status: 'under_review'
}];
const revenueTrend = Array.from({
  length: 30
}, (_, i) => ({
  name: `Day ${i + 1}`,
  value: Math.floor(Math.random() * 5000) + 10000
}));
export function EarningsPayouts() {
  const [activeTab, setActiveTab] = useState('all');
  const columns = [{
    header: 'Transaction ID',
    accessor: 'id',
    className: 'font-mono text-xs'
  }, {
    header: 'Type',
    accessor: 'type'
  }, {
    header: 'Party',
    accessor: 'party'
  }, {
    header: 'Amount',
    accessor: (row: any) => <span className={`font-medium ${row.amount.startsWith('+') ? 'text-green-600' : 'text-red-600'}`} data-id="element-479">
          {row.amount}
        </span>
  }, {
    header: 'Status',
    accessor: (row: any) => <StatusBadge status={row.status} data-id="element-480" />
  }, {
    header: 'Date',
    accessor: 'date'
  }];
  const filteredData = activeTab === 'all' ? transactions : transactions.filter(t => {
    if (activeTab === 'fares') return t.type === 'Ride Fare';
    if (activeTab === 'payouts') return t.type === 'Rider Payout';
    if (activeTab === 'refunds') return t.type === 'Refund';
    return true;
  });
  return <div className="space-y-6" data-id="element-481">
      <div className="flex justify-between items-center" data-id="element-482">
        <h2 className="text-xl font-bold text-slate-800 hidden" data-id="element-483">
          Earnings & Payouts
        </h2>
        <div className="flex-1" data-id="element-484"></div>
        <button className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm" data-id="element-485">
          <Download size={16} data-id="element-486" />
          Export Payout Report
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-487">
        <StatsCard title="Total Earnings (Mo)" value="₦284,500" trend={18} icon={DollarSign} color="gold" delay={0} data-id="element-488" />
        <StatsCard title="Total Payouts" value="₦213,375" trend={12} icon={Wallet} color="blue" delay={0.1} data-id="element-489" />
        <StatsCard title="Platform Commission" value="₦71,125" trend={22} icon={TrendingUp} color="green" delay={0.2} data-id="element-490" />
        <StatsCard title="Pending Payouts" value="₦12,400" trend={-5} icon={CreditCard} color="purple" delay={0.3} data-id="element-491" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-492">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-493">
          <h3 className="text-lg font-bold text-slate-800 mb-6" data-id="element-494">
            Revenue Trend (30 Days)
          </h3>
          <div className="h-64 w-full" data-id="element-495">
            <ResponsiveContainer width="100%" height="100%" data-id="element-496">
              <AreaChart data={revenueTrend} data-id="element-497">
                <defs data-id="element-498">
                  <linearGradient id="colorRevenueMain" x1="0" y1="0" x2="0" y2="1" data-id="element-499">
                    <stop offset="5%" stopColor="#C9956B" stopOpacity={0.3} data-id="element-500" />
                    <stop offset="95%" stopColor="#C9956B" stopOpacity={0} data-id="element-501" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-502" />
                <XAxis dataKey="name" hide data-id="element-503" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} tickFormatter={value => `₦${value / 1000}k`} data-id="element-504" />
                <Tooltip contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #E2E8F0'
              }} itemStyle={{
                color: '#C9956B',
                fontWeight: 600
              }} data-id="element-505" />
                <Area type="monotone" dataKey="value" stroke="#C9956B" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenueMain)" data-id="element-506" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Flagged Earnings Alert */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-507">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-508">
            <AlertTriangle className="text-red-500" size={20} data-id="element-509" />
            Flagged Earnings
          </h3>
          <div className="space-y-4" data-id="element-510">
            {flaggedEarnings.map(item => <div key={item.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50" data-id="element-511">
                <div className="flex justify-between items-start mb-1" data-id="element-512">
                  <span className="font-medium text-slate-900" data-id="element-513">
                    {item.rider}
                  </span>
                  <StatusBadge status={item.status} data-id="element-514" />
                </div>
                <p className="text-sm text-red-600 mb-2" data-id="element-515">{item.reason}</p>
                <p className="text-xs text-slate-400 mb-3" data-id="element-516">{item.date}</p>
                <div className="flex gap-2" data-id="element-517">
                  <button className="flex-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium py-1.5 rounded hover:bg-slate-50" data-id="element-518">
                    Investigate
                  </button>
                  <button className="flex-1 bg-white border border-slate-200 text-green-600 text-xs font-medium py-1.5 rounded hover:bg-green-50" data-id="element-519">
                    Clear Flag
                  </button>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>

      <div className="flex border-b border-slate-200" data-id="element-520">
        <button onClick={() => setActiveTab('all')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'all' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-521">
          All Transactions
        </button>
        <button onClick={() => setActiveTab('fares')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'fares' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-522">
          Ride Fares
        </button>
        <button onClick={() => setActiveTab('payouts')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'payouts' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-523">
          Payouts
        </button>
        <button onClick={() => setActiveTab('refunds')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'refunds' ? 'border-famo-gold text-famo-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-524">
          Refunds
        </button>
      </div>

      <motion.div key={activeTab} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} data-id="element-525">
        <DataTable data={filteredData} columns={columns} searchPlaceholder="Search transactions..." actions={row => <>
              <button className="p-1.5 text-slate-500 hover:text-famo-navy hover:bg-slate-100 rounded transition-colors" title="View Details" data-id="element-527">
                <Eye size={18} data-id="element-528" />
              </button>
              <button className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Flag Transaction" data-id="element-529">
                <Flag size={18} data-id="element-530" />
              </button>
            </>} data-id="element-526" />
      </motion.div>
    </div>;
}